import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, values } from 'lodash';

import type { Box } from '@flow/libs/data-validators/entity-schemas/document-box';
import { getGuestToken } from '@flow/libs/agreement/selectors';
import agreements from '@app/reducers/entities/agreements';
import {
  BoxOrder,
  Data,
  getAcceptedSuggestions,
  getPristineContractData,
  getPristineState,
  getRejectedSuggestions,
} from '@app/reducers/current-contract';
import useCurrentBoxes from '@app/hooks/use-current-boxes';
import useCurrentData from '@app/hooks/use-current-data';
import useCurrentBoxOrder from '@app/hooks/use-current-box-order';

import {
  getBoxOrderChangedValues,
  getDataFieldChangedValues,
  getNonPristineBoxesMap,
  getNonPristineDataFieldsMap,
  hasValidContractChanges,
} from '@app/components/document-call-to-actions/actions/buttons-call-to-action/actions/save/helpers';

export type NotificationData =
  | {
      message: string;
      subject: string;
    } // Complete notification data
  | {
      message?: string;
      subject?: string;
    }; // Partial or empty notification data

type AgreementData = {
  boxOrder?: BoxOrder;
  boxes?: Box[];
  data?: Data;
  guestToken?: string;
  notificationTempFlag?: boolean;
  subject?: string;
  message?: string;
  suggestionsToBeAccepted?: {
    suggestionId: number;
    annotation: Node[];
  }[];
  suggestionsToBeRejected?: {
    suggestionId: number;
    annotation: Node[];
  }[];
};

type Props = {
  agreementId: number;
  onSuccess: (props: any) => void;
};

export const useSaveAgreementHandler = ({ agreementId, onSuccess }: Props) => {
  const dispatch = useDispatch();
  const boxMap = useCurrentBoxes();
  const boxOrder = useCurrentBoxOrder();
  const data = useCurrentData();
  const pristineState = useSelector(getPristineState);
  const pristineContract = useSelector(getPristineContractData);
  const guestToken = useSelector(getGuestToken);
  const acceptedSuggestions = useSelector(getAcceptedSuggestions);
  const rejectedSuggestions = useSelector(getRejectedSuggestions);

  const prepareAgreementData = useCallback(
    (notificationData: NotificationData = {}) => {
      const isBoxOrderPristine = pristineState?.boxOrder;
      const nonPristineBoxes = getNonPristineBoxesMap(boxMap, pristineState);
      const pristineData = pristineContract?.data;
      const nonPristineData = getNonPristineDataFieldsMap(data, pristineState);
      const isGuest = Boolean(guestToken);
      const hasValidChanges = hasValidContractChanges(
        rejectedSuggestions,
        acceptedSuggestions,
        pristineState,
        boxMap,
        pristineContract,
      );

      const agreementData = {
        /**
         * TODO: notificationTempFlag - will be removed soon (OF-12389)
         * false if we want to enforce the notification
         * true if we want to skip the notification
         */
        notificationTempFlag: !hasValidChanges,
      } as AgreementData;

      if (!isBoxOrderPristine) {
        agreementData.boxOrder = getBoxOrderChangedValues(boxOrder, isBoxOrderPristine);
      }

      const changedBoxes = values(nonPristineBoxes);
      if (!isEmpty(changedBoxes)) {
        agreementData.boxes = changedBoxes;
      }

      const changedData = getDataFieldChangedValues(nonPristineData, pristineData);
      if (!isEmpty(changedData)) {
        agreementData.data = changedData;
      }

      if (isGuest) {
        agreementData.guestToken = guestToken;
      }

      if (!isEmpty(notificationData)) {
        agreementData.subject = notificationData.subject;
        agreementData.message = notificationData.message;
      }

      if (!isEmpty(acceptedSuggestions)) {
        agreementData.suggestionsToBeAccepted = acceptedSuggestions.map(({ id, nodes }) => ({
          suggestionId: id,
          annotation: nodes,
        }));
      }

      if (!isEmpty(rejectedSuggestions)) {
        agreementData.suggestionsToBeRejected = rejectedSuggestions.map(({ id, nodes }) => ({
          suggestionId: id,
          annotation: nodes,
        }));
      }

      return agreementData;
    },
    [
      boxMap,
      boxOrder,
      data,
      pristineState,
      pristineContract,
      guestToken,
      acceptedSuggestions,
      rejectedSuggestions,
    ],
  );

  const dispatchSaveAgreement = useCallback(
    (agreementData?: AgreementData) => {
      dispatch(
        agreements.saveAgreement({
          id: agreementId,
          data: agreementData,
          pipe: {
            onSuccess,
          },
        }),
      );
    },
    [
      agreementId,
      dispatch,
      onSuccess,
    ],
  );

  const saveAgreementHandler = useCallback(
    (notificationData?: NotificationData) => {
      const agreementData = prepareAgreementData(notificationData);
      dispatchSaveAgreement(agreementData);
    },
    [
      prepareAgreementData,
      dispatchSaveAgreement,
    ],
  );

  return {
    saveAgreementHandler,
  };
};
